import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { VscPerson, VscCommentDiscussion, VscSearch, VscTrash, VscClose, VscEdit, VscCheck } from 'react-icons/vsc';
import { BsFileEarmark } from 'react-icons/bs';
import { getMessages, sendMessage, markRead, getChatUsers, deleteMessage, editMessage } from '../../api/chat';
import ChatInput from './ChatInput';
import Avatar from '../common/Avatar';
import './Chat.css';

const ROLE_LABELS = { admin: 'Admin', engineer: 'Engineer', manager: 'Manager', foreman: 'Foreman', 'team-leader': 'Team Leader', standard: 'Standard', client: 'Client', other: 'Other' };

function fmtTime(dateStr) {
  const d   = new Date(dateStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' +
         d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function parseNbNo(tag) { const m = tag.match(/^(\d+)-/); return m ? m[1] : null; }
function stripNbNo(tag) { return tag.replace(/^\d+-/, ''); }

function findProject(tag, projects) {
  const nbNo = parseNbNo(tag);
  return nbNo ? projects?.find(p => String(p.newbuildingNo) === nbNo) : null;
}

function handleCableLink(tag, projects, onOpenTab) {
  const project = findProject(tag, projects);
  if (project) onOpenTab(`cable-register-${project._id}`, 'Cables', { type: 'cable-register', project, highlightTag: stripNbNo(tag) });
}

function handleItemLink(tag, projects, onOpenTab) {
  const project = findProject(tag, projects);
  if (project) onOpenTab(`load-list-${project._id}`, 'Items', { type: 'load-list', project, highlightTag: stripNbNo(tag) });
}

function handleJobPackLink(tag, projects, onOpenTab) {
  const project = findProject(tag, projects);
  if (project) {
    const pkgNum = tag.replace(/^\d+-/, '');
    onOpenTab(`job-packages-${project._id}`, 'Job Packages', { type: 'job-packages', project, highlightPkg: pkgNum });
  }
}

function renderContent(text, projects, onOpenTab) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  const re = /(@all\b)|(@\w+)|(%([\w.-]+))|(#([\w.-]+))|(&([\w.-]+))/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) {
      parts.push(<span key={m.index} className="chat-mention-all">@all</span>);
    } else if (m[2]) {
      parts.push(<span key={m.index} className="chat-mention-user">{m[2]}</span>);
    } else if (m[3]) {
      const tag = m[4];
      const project = findProject(tag, projects);
      if (project) {
        parts.push(<span key={m.index} className="chat-link" onClick={() => handleCableLink(tag, projects, onOpenTab)}>%{tag}</span>);
      } else {
        parts.push(m[0]);
      }
    } else if (m[5]) {
      const tag = m[6];
      const project = findProject(tag, projects);
      if (project) {
        parts.push(<span key={m.index} className="chat-link" onClick={() => handleItemLink(tag, projects, onOpenTab)}>#{tag}</span>);
      } else {
        parts.push(m[0]);
      }
    } else if (m[7]) {
      const tag = m[8];
      const project = findProject(tag, projects);
      if (project) {
        parts.push(<span key={m.index} className="chat-link" onClick={() => handleJobPackLink(tag, projects, onOpenTab)}>&{tag}</span>);
      } else {
        parts.push(m[0]);
      }
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function canUserDelete(msg, idx, messages, currentUser, role) {
  if (!currentUser) return false;
  if (['admin', 'engineer', 'manager'].includes(role)) return true;
  if (msg.sender?._id !== currentUser.id) return false;
  // Own message: only if no one else replied after it
  for (let j = idx + 1; j < messages.length; j++) {
    if (messages[j].sender?._id !== currentUser.id) return false;
  }
  return true;
}

function MembersTooltip({ group, allUsers }) {
  const [show, setShow] = useState(false);
  const count   = group.members?.length ?? 0;
  const members = allUsers.filter(u => (group.members || []).includes(u._id));
  return (
    <span
      className="chat-members-hover"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {count} member{count !== 1 ? 's' : ''}
      {show && members.length > 0 && (
        <div className="chat-members-tooltip">
          {members.map(u => (
            <div key={u._id} className={`chat-members-tooltip-item role-${u.role}`}>
              {u.username}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

function GroupHeader({ group, allUsers, currentUserId, onlineUsers }) {
  if (group.type === 'dm') {
    const otherId  = group.members?.find(id => id !== currentUserId);
    const other    = allUsers.find(u => u._id === otherId);
    const isOnline = otherId && (onlineUsers || []).includes(otherId);
    return (
      <>
        <VscPerson size={18} style={{ color: 'var(--text-muted)' }} />
        <div>
          <div className="chat-window-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className={`online-dot${isOnline ? ' online' : ''}`} />
            {other ? (other.displayName || other.username) : 'Direct Message'}
          </div>
          {other && <div className="chat-window-subtitle">{ROLE_LABELS[other.role]}</div>}
        </div>
      </>
    );
  }
  return (
    <>
      <VscCommentDiscussion size={18} style={{ color: 'var(--text-muted)' }} />
      <div>
        <div className="chat-window-title">{group.name}</div>
        <div className="chat-window-subtitle">
          <MembersTooltip group={group} allUsers={allUsers} />
        </div>
      </div>
    </>
  );
}

function Lightbox({ url, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div className="lightbox-backdrop" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}><VscClose size={18} /></button>
      <img
        src={url}
        className="lightbox-img"
        alt=""
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}

function ChatWindow({ group, isActive = true, socket, currentUser, projects, onOpenTab, role, onlineUsers = [] }) {
  // Background chat tabs stay mounted — only mark read while the tab is actually visible
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const canMarkRead = () => isActiveRef.current && document.visibilityState === 'visible';

  const [messages, setMessages]     = useState([]);
  const [offline, setOffline]       = useState(false);
  const [hasMore, setHasMore]       = useState(true);
  const [loading, setLoading]       = useState(false);
  const [allUsers, setAllUsers]     = useState([]);
  const [lightbox, setLightbox]     = useState(null);
  const [editingId, setEditingId]   = useState(null);
  const [editText, setEditText]     = useState('');
  const [typingUsers, setTypingUsers] = useState({});   // { userId: username }
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const bottomRef      = useRef(null);
  const messagesRef    = useRef(null);
  const groupIdRef     = useRef(group._id);
  const typingTimers   = useRef({});
  const pinnedToBottom = useRef(true);
  const scrollPending  = useRef(false);   // jump to the bottom once the reloaded messages render
  const wasActiveRef   = useRef(false);

  const scrollToBottom = (behavior = 'smooth') =>
    bottomRef.current?.scrollIntoView({ behavior });

  const onMessagesScroll = (e) => {
    const el = e.currentTarget;
    pinnedToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const onImageLoad = () => {
    if (pinnedToBottom.current) scrollToBottom('instant');
  };

  useEffect(() => { getChatUsers().then(r => setAllUsers(r.data)).catch(() => {}); }, []);

  const loadMessages = useCallback(async (before) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (before) params.before = before;
      const res  = await getMessages(group._id, params);
      const msgs = res.data;
      if (before) {
        setMessages(prev => [...msgs, ...prev]);
      } else {
        if (pinnedToBottom.current) scrollPending.current = true;
        setMessages(msgs);
        setHasMore(msgs.length === 50);
      }
      if (msgs.length < 50) setHasMore(false);
    } catch {}
    setLoading(false);
  }, [group._id]);

  useEffect(() => {
    groupIdRef.current = group._id;
    pinnedToBottom.current = true;
    setMessages([]);
    setHasMore(true);
    setSearchQuery('');
    setSearchOpen(false);
    setTypingUsers({});
    loadMessages();
  }, [group._id, loadMessages]);

  useEffect(() => {
    if (canMarkRead()) markRead(group._id).catch(() => {});
  }, [group._id, isActive]);

  // Show the newest messages when the chat is opened or its tab re-shown (messages that
  // arrived while the tab was hidden are otherwise below the fold), and after a reload
  useLayoutEffect(() => {
    if (!isActive) { wasActiveRef.current = false; return; }
    if (!wasActiveRef.current || scrollPending.current) {
      const el = messagesRef.current;
      if (el) el.scrollTop = el.scrollHeight;
      pinnedToBottom.current = true;
    }
    wasActiveRef.current = true;
    scrollPending.current = false;
  }, [messages, isActive]);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const onNew = ({ message }) => {
      if (message.group !== groupIdRef.current) return;
      setMessages(prev => prev.find(m => m._id === message._id) ? prev : [...prev, message]);
      if (message.sender?._id) {
        setTypingUsers(prev => { const n = { ...prev }; delete n[message.sender._id]; return n; });
      }
      if (canMarkRead()) markRead(groupIdRef.current).catch(() => {});
      setTimeout(() => { if (pinnedToBottom.current) scrollToBottom(); }, 50);
    };

    const onDeleted = ({ messageId }) => {
      setMessages(prev => prev.filter(m => m._id !== messageId));
    };

    const onEdited = ({ message }) => {
      setMessages(prev => prev.map(m => m._id === message._id ? message : m));
    };

    const onTyping = ({ groupId, userId, username }) => {
      if (groupId !== groupIdRef.current || userId === currentUser?.id) return;
      setTypingUsers(prev => ({ ...prev, [userId]: username }));
      clearTimeout(typingTimers.current[userId]);
      typingTimers.current[userId] = setTimeout(() => {
        setTypingUsers(prev => { const n = { ...prev }; delete n[userId]; return n; });
      }, 3000);
    };

    // On reconnect (e.g. mobile returning from background) reload so missed messages appear
    const onReconnect = () => {
      loadMessages();
      if (canMarkRead()) markRead(groupIdRef.current).catch(() => {});
    };

    socket.on('message:new',     onNew);
    socket.on('message:deleted', onDeleted);
    socket.on('message:edited',  onEdited);
    socket.on('typing:start',    onTyping);
    socket.on('connect',         onReconnect);
    return () => {
      socket.off('message:new',     onNew);
      socket.off('message:deleted', onDeleted);
      socket.off('message:edited',  onEdited);
      socket.off('typing:start',    onTyping);
      socket.off('connect',         onReconnect);
    };
  }, [socket, currentUser?.id, loadMessages]);

  // Connection lost: show a notice (after a grace period, so quick reconnects don't flash)
  // and poll for new messages until the socket is back
  useEffect(() => {
    if (!socket) return;
    let graceTimer;
    const onDown = () => { graceTimer = setTimeout(() => setOffline(true), 4000); };
    const onUp   = () => { clearTimeout(graceTimer); setOffline(false); };
    socket.on('disconnect', onDown);
    socket.on('connect',    onUp);
    if (!socket.connected) onDown();
    const pollTimer = setInterval(() => {
      if (socket.connected || !canMarkRead()) return;
      loadMessages();
      markRead(groupIdRef.current).catch(() => {});
    }, 15000);
    return () => {
      clearTimeout(graceTimer);
      clearInterval(pollTimer);
      socket.off('disconnect', onDown);
      socket.off('connect',    onUp);
    };
  }, [socket, loadMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload when tab becomes visible again (mobile background → foreground)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadMessages();
        if (canMarkRead()) markRead(groupIdRef.current).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadMessages]);

  const handleSend = async (content, attachments = []) => {
    try {
      const res = await sendMessage(group._id, content, attachments);
      setMessages(prev => prev.find(m => m._id === res.data._id) ? prev : [...prev, res.data]);
      pinnedToBottom.current = true;
      setTimeout(() => scrollToBottom(), 30);
    } catch {}
  };

  const handleDelete = async (msgId) => {
    try {
      await deleteMessage(msgId);
      setMessages(prev => prev.filter(m => m._id !== msgId));
    } catch (err) {
      alert(err?.response?.data?.msg || 'Could not delete message.');
    }
  };

  const startEdit = (msg) => {
    setEditingId(msg._id);
    setEditText(msg.content);
  };

  const cancelEdit = () => { setEditingId(null); setEditText(''); };

  const handleEditSave = async (msgId) => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    try {
      const res = await editMessage(msgId, trimmed);
      setMessages(prev => prev.map(m => m._id === msgId ? res.data : m));
      cancelEdit();
    } catch (err) {
      alert(err?.response?.data?.msg || 'Could not edit message.');
    }
  };

  const handleLoadMore = () => {
    if (!messages.length) return;
    loadMessages(messages[0].createdAt);
  };

  const typingList   = Object.values(typingUsers);
  const displayMsgs  = searchQuery.trim()
    ? messages.filter(m => m.content?.toLowerCase().includes(searchQuery.toLowerCase()))
    : messages;

  return (
    <div className="chat-window">
      {/* Header */}
      <div className="chat-window-header">
        <GroupHeader group={group} allUsers={allUsers} currentUserId={currentUser?.id} onlineUsers={onlineUsers} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button
            className={`btn-icon${searchOpen ? ' active' : ''}`}
            title="Search messages"
            onClick={() => { setSearchOpen(v => !v); setSearchQuery(''); }}
          >
            <VscSearch size={15} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="chat-search-bar">
          <VscSearch size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            autoFocus
            className="chat-search-input"
            placeholder="Search messages…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
          />
          {searchQuery && (
            <span className="chat-search-count">{displayMsgs.length} result{displayMsgs.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      )}

      {offline && <div className="chat-offline-banner">Reconnecting… new messages may be delayed</div>}

      {/* Messages */}
      <div className="chat-messages" ref={messagesRef} onScroll={onMessagesScroll}>
        {hasMore && !loading && (
          <button className="chat-load-more" onClick={handleLoadMore}>Load earlier messages</button>
        )}
        {loading && messages.length === 0 && (
          <div style={{ padding: '20px 16px', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
        )}

        {displayMsgs.map((msg, i) => {
          const canDel  = canUserDelete(msg, i, displayMsgs, currentUser, role);
          const canEdit = currentUser && msg.sender?._id === currentUser.id;
          const isEditing = editingId === msg._id;
          const isImg   = (a) => a.mimetype?.startsWith('image/') ||
            (!a.mimetype && /\.(jpg|jpeg|png|gif|webp)$/i.test(a.url));
          const senderName = msg.sender?.displayName || msg.sender?.username || 'Unknown';
          return (
            <div key={msg._id} className="chat-message">
              <div className="chat-message-meta">
                <Avatar user={msg.sender} size={22} />
                <span className={`chat-sender role-${msg.sender?.role}`}>{senderName}</span>
                <span className="chat-timestamp">{fmtTime(msg.createdAt)}</span>
                {msg.edited && <span className="chat-edited">(edited)</span>}
              </div>
              <div className="chat-message-body">
                {isEditing ? (
                  <div className="chat-edit-wrap">
                    <textarea
                      className="chat-edit-input"
                      value={editText}
                      autoFocus
                      onChange={e => setEditText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSave(msg._id); }
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                    <div className="chat-edit-actions">
                      <button className="btn-icon" title="Save" onClick={() => handleEditSave(msg._id)}><VscCheck size={13} /></button>
                      <button className="btn-icon" title="Cancel" onClick={cancelEdit}><VscClose size={13} /></button>
                    </div>
                  </div>
                ) : (
                  <>
                    {msg.content && (
                      <span className="chat-content">
                        {renderContent(msg.content, projects, onOpenTab)}
                      </span>
                    )}
                    {msg.attachments?.length > 0 && (
                      <div className="chat-attachments">
                        {msg.attachments.map((a, ai) => isImg(a) ? (
                          <img key={ai} src={a.url} className="chat-img" alt=""
                            onLoad={onImageLoad}
                            onClick={() => setLightbox(a.url)} />
                        ) : (
                          <a key={ai} href={a.url} download={a.name} className="chat-file-attach"
                            onClick={e => e.stopPropagation()}>
                            <BsFileEarmark size={13} />
                            <span>{a.name || 'File'}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="chat-msg-actions">
                {canEdit && !isEditing && (
                  <button className="chat-delete-btn" title="Edit message" onClick={() => startEdit(msg)}>
                    <VscEdit size={12} />
                  </button>
                )}
                {canDel && !isEditing && (
                  <button className="chat-delete-btn" title="Delete message" onClick={() => handleDelete(msg._id)}>
                    <VscTrash size={12} />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* Typing indicator */}
      {typingList.length > 0 && (
        <div className="chat-typing">
          <span className="chat-typing-dots"><span /><span /><span /></span>
          {typingList.length === 1
            ? `${typingList[0]} is typing…`
            : `${typingList.join(', ')} are typing…`}
        </div>
      )}

      <ChatInput onSend={handleSend} allUsers={allUsers} socket={socket} groupId={group._id} />

      {lightbox && <Lightbox url={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

export default ChatWindow;
